import produce from 'immer';
import { createModel } from '@rematch/core';
import { mergeClients as apiMergeClients } from '../../api/Providers/MergeClients';
import {
  updateClient as apiUpdateClient,
  getClient,
  getAllClients,
  deleteClient,
  clearAllClientsCache,
  createMultipleClients as apiCreateMultipleClients,
  blockClient,
  unblockClient,
} from '../../api/Providers/Clients';
import type { HistoryRecord, IProviderClient } from '../../api/Providers/Clients/types';
import type { RootModel } from '../models';
import { PartialProviderClient } from '../../modules/provider/Client';
import {
  canSendClientApptOptInSms,
  ClientSMSStatus,
  getStatus as getSMSStatus,
} from '../../modules/api/clients/sms';
import { ReminderType } from '../../api/Providers/Appointments';
import { IDRFResponse } from '../../types';
import { getClientHistory } from '../../api/Providers/Clients/History';
import { isFutureAppointment } from '../../modules/ProAppointmentState';

let finishedInitialLoad: boolean = false;

interface ClientHistory {
  loading: boolean;
  items: HistoryRecord[];
}

export type ProviderClientsState = {
  providerId: number | null;
  loading: boolean;
  clients: Record<number, IProviderClient>;
  clientSmsInviteStatus: Record<number, ClientSMSStatus>;
  clientHistory: Record<number, ClientHistory>
};

type ProviderAndClientId = {
  providerId: number | string;
  clientId: number;
};

interface ClientAndAppointmentId {
  clientId: number;
  appointmentId: number;
}

type LoadAllPayload = {
  providerId: number | string;
};

type LoadClientPayload = ProviderAndClientId;

type RemoveClientPayload = ProviderAndClientId;

type BlockClientPayload = ProviderAndClientId;

type UnblockClientPayload = ProviderAndClientId;

type MergeClientPayload = {
  providerId: number | string;
  clientIdA: number;
  clientIdB: number;
};

type UpdateClientPayload = {
  providerId: number | string;
  client: PartialProviderClient & Pick<IProviderClient, 'id'>;
};

type AddMultipleClientsPayload = {
  providerId: number | string;
  clients: PartialProviderClient[];
};

type UpdateReminderPreferencePayload = {
  providerId: number | string;
  clientId: number;
  reminderPreference?: ReminderType;
};

type OnClientLoadedPayload = {
  providerId: number | string;
  client: IProviderClient;
};

type OnSmsStatusLoadedPayload = {
  providerId: number | string;
  clientId: number | string;
  status: ClientSMSStatus;
};

interface OnClientHistoryLoadingPayload {
  clientId: number | string;
}

interface OnClientHistoryLoadedPayload {
  clientId: number | string;
  records: HistoryRecord[];
}

type OnAllClientsLoadedPayload = {
  providerId: number | string;
};

type OnClientsPageLoadedPayload = {
  clients: Array<IProviderClient>;
};

type OnClientBlockUpdatedPayload = {
  clientId: number;
  blocked: boolean;
};

interface LoadClientHistoryPayload {
  clientId: number | string;
  providerId: number | string;
}

async function fetchAllClients(
  dispatch,
  providerId: number,
  currentPage: number = 0,
  pageCache: Array<IProviderClient> = [],
  nextUrl?: string,
): Promise<void> {
  const clientResponse = await getAllClients(providerId, nextUrl);
  let nextPageCache: Array<IProviderClient> = clientResponse.results;
  if (currentPage % 2 === 0) {
    // if we've assembled two whole pages, let's get them committed to redux
    await dispatch.providerClients.onClientsPageLoaded({
      clients: [
        ...pageCache,
        ...nextPageCache,
      ],
    });
    nextPageCache = [];
  } else if (!clientResponse.next) {
    // if we haven't assembled two pages but there aren't anymore results, let's commit what
    // we have (the last page)
    await dispatch.providerClients.onClientsPageLoaded({
      clients: nextPageCache,
    });
  }

  if (clientResponse.next) {
    // if there are more results and we haven't assembled two pages, cache our results and
    // wait for the next set before committing
    return fetchAllClients(
      dispatch,
      providerId,
      currentPage + 1,
      nextPageCache,
      clientResponse.next,
    );
  }

  return undefined;
}

function getInitialState(): ProviderClientsState {
  return {
    loading: false,
    providerId: null,
    clients: {},
    clientSmsInviteStatus: {},
    clientHistory: {},
  };
}

export const providerClients = createModel<RootModel>()({
  name: 'providerClients',

  state: getInitialState(),

  reducers: {
    'user/logout': () => getInitialState(),

    onClientLoading: (state: ProviderClientsState): ProviderClientsState => ({
      ...state,
      loading: true,
    }),

    onClientLoaded: produce<ProviderClientsState, [OnClientLoadedPayload]>((
      state: ProviderClientsState,
      payload: OnClientLoadedPayload,
    ) => {
      const providerId = Number(payload.providerId);

      if (providerId === state.providerId) {
        state.clients[payload.client.id] = payload.client;
      } else {
        state.providerId = providerId;
        state.clients = { [payload.client.id]: payload.client };
      }

      state.loading = false;
    }),

    onSMSStatusLoaded: produce<ProviderClientsState, [OnSmsStatusLoadedPayload]>((
      state: ProviderClientsState,
      payload: OnSmsStatusLoadedPayload,
    ) => {
      const providerId = Number(payload.providerId);

      if (providerId === state.providerId) {
        state.clientSmsInviteStatus[payload.clientId] = payload.status;
      } else {
        state.providerId = providerId;
        state.clientSmsInviteStatus = { [Number(payload.clientId)]: payload.status };
      }

      state.loading = false;
    }),

    onClientsPageLoaded: (
      state: ProviderClientsState,
      payload: OnClientsPageLoadedPayload,
    ): ProviderClientsState => {
      const clientMap: Record<number, IProviderClient> = { ...(state.clients || {}) };

      payload.clients
        .filter(client => client.provider === state.providerId)
        .forEach(client => {
          clientMap[client.id] = client;
        });

      return {
        ...state,
        clients: clientMap,
      };
    },

    onAllClientsLoading: (
      state: ProviderClientsState,
      payload: LoadAllPayload,
    ): ProviderClientsState => ({
      ...state,
      providerId: Number(payload.providerId),
      loading: true,
    }),

    onAllClientsLoaded: (
      state: ProviderClientsState,
      payload: OnAllClientsLoadedPayload,
    ): ProviderClientsState => {
      finishedInitialLoad = true;

      return ({
        loading: false,
        providerId: Number(payload.providerId),
        // since each page has already added the clients to the store
        // we don't need to do it here
        clients: state.clients,
        clientSmsInviteStatus: {},
        clientHistory: {},
      });
    },

    onClientBlockUpdated: produce<ProviderClientsState, [OnClientBlockUpdatedPayload]>((
      state: ProviderClientsState,
      payload: OnClientBlockUpdatedPayload,
    ) => {
      state.clients[payload.clientId].blocked = payload.blocked;
    }),

    onClientRemoved: produce<ProviderClientsState, [RemoveClientPayload]>((
      state: ProviderClientsState,
      payload: RemoveClientPayload,
    ) => {
      delete state.clients[payload.clientId];
    }),

    onReloading: (): ProviderClientsState => {
      finishedInitialLoad = false;

      return ({
        loading: false,
        providerId: null,
        clients: {},
        clientSmsInviteStatus: {},
        clientHistory: {},
      });
    },

    onClientHistoryLoading: produce<ProviderClientsState, [OnClientHistoryLoadingPayload]>((
      state: ProviderClientsState,
      payload: OnClientHistoryLoadingPayload,
    ) => {
      const clientId = Number(payload.clientId);
      const clientHistory: ClientHistory = state.clientHistory[clientId];

      if (clientHistory) {
        clientHistory.loading = true;
      } else {
        state.clientHistory[clientId] = {
          loading: true,
          items: [],
        };
      }
    }),

    onClientHistoryLoaded: produce<ProviderClientsState, [OnClientHistoryLoadedPayload]>((
      state: ProviderClientsState,
      payload: OnClientHistoryLoadedPayload,
    ) => {
      const clientId = Number(payload.clientId);

      state.clientHistory[clientId] = {
        loading: false,
        items: payload.records,
      };
    }),
  },

  effects: dispatch => ({
    async loadAll({
      providerId,
    }: LoadAllPayload, rootState): Promise<ProviderClientsState['clients']> {
      if (
        !finishedInitialLoad
        || providerId !== rootState.providerClients.providerId
        || !Object.keys(rootState.providerClients.clients).length
      ) {
        // if the provider ID is different OR there are no clients in the list, refresh from DB
        await dispatch.providerClients.onAllClientsLoading({ providerId });

        await fetchAllClients(
          dispatch,
          Number(providerId),
        );

        await dispatch.providerClients.onAllClientsLoaded({
          providerId,
        });
      }

      return rootState.providerClients.clients;
    },

    async reloadAll({ providerId }: LoadAllPayload) {
      await dispatch.providerClients.onReloading();
      clearAllClientsCache();
      await dispatch.providerClients.loadAll({ providerId });
    },

    async loadClient({ providerId, clientId }: LoadClientPayload) {
      await dispatch.providerClients.onClientLoading();

      let client: IProviderClient;

      try {
        client = await getClient(providerId, clientId);

        await dispatch.providerClients.onClientLoaded({
          providerId,
          client,
        });
      } catch (err) {
        if (err?.code === 404) {
          // If the response code is 404, remove this client and continue with the error pipeline
          await dispatch.providerClients.onClientRemoved({ providerId, clientId });
        }

        throw err;
      }

      return client;
    },

    async loadClientSMSStatus(
      { providerId, clientId }: LoadClientPayload,
      rootState,
    ): Promise<ClientSMSStatus> {
      let client: IProviderClient = rootState.providerClients.clients[clientId];

      if (!client) {
        client = await getClient(providerId, clientId);
        await dispatch.providerClients.onClientLoaded({ providerId, client });
      }

      await dispatch.providerClients.onClientLoading();

      const status = getSMSStatus(await canSendClientApptOptInSms(clientId));

      await dispatch.providerClients.onSMSStatusLoaded({
        providerId,
        clientId,
        status,
      });

      return status;
    },

    async updateReminderPreference(
      {
        clientId,
        reminderPreference,
        providerId,
      }: UpdateReminderPreferencePayload,
      rootState,
    ): Promise<void> {
      const newPreference: ReminderType | null = reminderPreference || null;
      const oldPreference = rootState.providerClients.clients[clientId].reminder_preference;
      if (oldPreference !== undefined && (oldPreference !== newPreference)) {
        await dispatch.providerClients.updateClient({
          providerId,
          client: {
            id: clientId,
            reminder_preference: newPreference,
          },
        });

        if (
          clientId
          && (
            reminderPreference === ReminderType.SmsEmailReminder
            || reminderPreference === ReminderType.SmsOnlyReminder
          )
        ) {
          dispatch.providerClients.loadClientSMSStatus({ providerId, clientId });
        }
      }
    },

    async removeClient({ providerId, clientId }: RemoveClientPayload) {
      await deleteClient(Number(providerId), clientId);

      await dispatch.providerClients.onClientRemoved({ providerId, clientId });
    },

    /**
     * Takes two client IDs and merges them, yielding the ID of the resulting client
     */
    async mergeClients({
      providerId, clientIdA, clientIdB,
    }: MergeClientPayload): Promise<number> {
      const result = await apiMergeClients(Number(providerId), clientIdA, clientIdB);

      await Promise.all([
        dispatch.providerClients.onClientRemoved({ providerId, clientId: clientIdA }),
        dispatch.providerClients.onClientRemoved({ providerId, clientId: clientIdB }),
        dispatch.providerClients.loadClient({ providerId, clientId: result.new_client }),
      ]);

      return result.new_client;
    },

    async updateClient({ providerId, client }: UpdateClientPayload): Promise<IProviderClient> {
      const clientPayload = { phones: [], ...client };

      const result = await apiUpdateClient(providerId, clientPayload);

      await dispatch.providerClients.onClientLoaded({ providerId, client: result });

      return result;
    },

    async blockClient({ providerId, clientId }: BlockClientPayload): Promise<void> {
      await blockClient(providerId, clientId);

      await dispatch.providerClients.onClientBlockUpdated({ clientId, blocked: true });
    },

    async unblockClient({ providerId, clientId }: UnblockClientPayload): Promise<void> {
      await unblockClient(providerId, clientId);

      await dispatch.providerClients.onClientBlockUpdated({ clientId, blocked: false });
    },

    async createMultipleClients(
      { providerId, clients }: AddMultipleClientsPayload,
    ): Promise<Array<IProviderClient>> {
      const resultClients = await apiCreateMultipleClients(
        Number(providerId),
        clients,
      );

      await dispatch.providerClients.onClientsPageLoaded({
        clients: resultClients,
      });

      return resultClients;
    },

    async loadClientHistory(payload: LoadClientHistoryPayload) {
      const clientId = Number(payload.clientId);
      const providerId = Number(payload.providerId);

      await dispatch.providerClients.onClientHistoryLoading({ clientId });

      const historyResponse: IDRFResponse<HistoryRecord> = await getClientHistory(
        providerId,
        clientId,
      );

      await dispatch.providerClients.onClientHistoryLoaded({
        clientId,
        records: historyResponse.results,
      });
    },
  }),

  selectors: (slice, createSelector, hasProps) => ({
    client: hasProps((_, clientId: number) => (
      createSelector(
        slice,
        (state: ProviderClientsState) => state.clients[clientId],
      )
    )),

    clientSMSStatus: hasProps((_, clientId: number) => (
      createSelector(
        slice,
        (state: ProviderClientsState): ClientSMSStatus => (
          state.clientSmsInviteStatus[clientId]
          || ClientSMSStatus.NoSmsPermissionHistory
        ),
      )
    )),

    sortedClients: () => (
      createSelector(
        slice((state: ProviderClientsState) => state.clients),
        slice((state: ProviderClientsState) => state.providerId),
        (clients: ProviderClientsState['clients'], providerId): IProviderClient[] => (
          // default sort is ASCII ascending, which should be sufficient
          Object.values(clients).filter(client => client.provider === providerId).sort()
        ),
      )
    ),

    historyForClient: hasProps((_, clientId: number) => (
      createSelector(
        slice,
        (state: ProviderClientsState): ClientHistory => (
          state.clientHistory[clientId] || { loading: false, items: [] }
        ),
      )
    )),

    filteredHistoryForClient: hasProps((_, clientId: number) => (
      createSelector(
        slice(state => state.clientHistory[clientId] || { loading: false, items: [] }),
        (state: ClientHistory): HistoryRecord[] => (
          state.items.filter(historyItem => (
            [
              'note',
              'photo',
              'appointment',
            ].includes(historyItem.type)
            && !(historyItem.type === 'appointment' && isFutureAppointment(historyItem))
          ))
        ),
      )
    )),

    filteredHistoryForAppointment: hasProps((
      _,
      { clientId, appointmentId }: ClientAndAppointmentId,
    ) => (
      createSelector(
        slice(state => state.clientHistory[clientId] || { loading: false, items: [] }),
        (state: ClientHistory): HistoryRecord[] => (
          state.items.filter(historyItem => (
            [
              'note',
              'photo',
              'appointment',
            ].includes(historyItem.type)
            && appointmentId !== historyItem.id
            && !(historyItem.type === 'appointment' && isFutureAppointment(historyItem))
          ))
        ),
      )
    )),
  }),
});
